import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useCallback, useId, useState } from "react";
import { Tooltip } from "react-tooltip";

import { Icon } from "~/assets/icons/Icon";
import Button from "~/components/mantine/button/Button";

import styles from "./CopyButton.module.scss";

interface CopyButtonProps {
  value: string;
  disabled?: boolean;
  buttonText?: string;
  copiedText?: string;
  copyTimeoutMs?: number;
  className?: string;
  viewMode?: "normal" | "mini";
}

export default function CopyButton({
  value,
  disabled = false,
  buttonText = "Copy",
  copiedText = "Copied!",
  copyTimeoutMs = 2000,
  className = "",
  viewMode = "normal",
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const tooltipId = useId();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, copyTimeoutMs);
    } catch {
      enqueueSnackbar("Failed to copy text", {
        variant: "error",
        persist: true,
      });
    }
  }, [value, copyTimeoutMs]);

  const icon = <Icon iconName={copied ? "CheckmarkIcon" : "CopyIcon"} />;

  if (viewMode === "mini") {
    return (
      <>
        <button
          type="button"
          onClick={handleCopy}
          disabled={disabled}
          className={clsx(styles.copyButtonMini, className)}
          aria-label={copied ? copiedText : buttonText}
          title={copied ? copiedText : buttonText}
          data-tooltip-id={tooltipId}
          data-tooltip-content={copied ? copiedText : buttonText}
        >
          {icon}
        </button>
        <Tooltip id={tooltipId} place="top" delayShow={200} />
      </>
    );
  }

  return (
    <Button
      className={className}
      variant="outlined"
      onClick={handleCopy}
      disabled={disabled}
    >
      <span>
        {icon}
        {copied ? copiedText : buttonText}
      </span>
    </Button>
  );
}
